import { useState, useEffect, useRef } from 'react';
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { Product, ProductVariant } from '../../types/product-variant';
import { useUI } from '../../context/UIContext';
import { useStock } from '../../context/StockContext';
import { faClock } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from 'react-i18next';

interface CartActionsProps {
    product: Product;
    isAdded: boolean;
    localQuantity: number;
    onAddToCart: () => void;
    onQuantityChange: (quantity: number) => void;
    showVariantError: boolean;
    inStock?: boolean;
    selectedVariant?: ProductVariant | null;
    /** Override stock count (e.g. per-ring-size). When set, bypasses StockContext. */
    stockOverride?: number;
    /** Override made-to-order flag (e.g. per-ring-size). When set, bypasses StockContext. */
    madeToOrderOverride?: boolean;
    /** True when a ring size must be selected before adding to cart */
    needsRingSize?: boolean;
}

const CartActions = ({
    product,
    isAdded,
    localQuantity,
    onAddToCart,
    onQuantityChange,
    showVariantError,
    inStock = true,
    selectedVariant,
    stockOverride,
    madeToOrderOverride,
    needsRingSize = false,
}: CartActionsProps) => {
    const { isCartOpen } = useUI();
    const { getStock, isMadeToOrder } = useStock();
    const { t } = useTranslation();
    const inlineRef = useRef<HTMLDivElement>(null);
    const [showFixedBar, setShowFixedBar] = useState(false);

    useEffect(() => {
        const el = inlineRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => setShowFixedBar(!entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const CartButton = () => {
        // Check if any variants are in stock or made-to-order when no variant is selected
        const getTotalVariantsStock = () => {
            if (product.variants && product.variants.length > 0) {
                return product.variants.reduce((total, variant) => {
                    return total + (getStock(product.id, variant.id) || 0);
                }, 0);
            }
            return getStock(product.id) || 0;
        };

        const getAnyVariantMadeToOrder = () => {
            if (product.variants && product.variants.length > 0) {
                return product.variants.some(variant => isMadeToOrder(product.id, variant.id));
            }
            return isMadeToOrder(product.id);
        };

        const hasVariants = Boolean(product.variants && product.variants.length > 0);

        // Use per-size overrides when provided (ring size selection), otherwise use StockContext
        const stock = stockOverride !== undefined
            ? stockOverride
            : selectedVariant
                ? getStock(product.id, selectedVariant.id) ?? 0
                : getTotalVariantsStock();

        const madeToOrder = madeToOrderOverride !== undefined
            ? madeToOrderOverride
            : selectedVariant
                ? isMadeToOrder(product.id, selectedVariant.id)
                : getAnyVariantMadeToOrder();

        const isOutOfStock = stock === 0 && !madeToOrder;
        const isMadeToOrderAvailable = stock === 0 && madeToOrder;

        // Disabled when variant or ring size not yet selected
        const needsSelection = (!selectedVariant && hasVariants) || needsRingSize;
        const selectionPrompt = needsRingSize ? t('ringSize.selectSize') : t('common.selectMaterial');

        // Out of stock - show disabled button
        if (isOutOfStock && !needsRingSize) {
            return (
                <motion.button
                    disabled
                    className="w-full px-8 py-3 rounded-lg shadow-md flex items-center justify-center space-x-2 bg-gray-400 text-white cursor-not-allowed"
                >
                    <span>{t('product.outOfStock')}</span>
                </motion.button>
            );
        }

        // Needs selection — greyed out with prompt (same pattern as variant selector)
        if (needsSelection) {
            return (
                <motion.button
                    disabled
                    className="w-full px-8 py-3 rounded-lg shadow-md flex items-center justify-center space-x-2 opacity-50 cursor-not-allowed bg-brand-blue text-white"
                >
                    <FontAwesomeIcon icon={faShoppingCart} />
                    <span>{selectionPrompt}</span>
                </motion.button>
            );
        }

        // Made to Order - show amber button with production time
        if (isMadeToOrderAvailable) {
            return (
                <div className="space-y-2">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onAddToCart}
                        className={`w-full px-8 py-3 rounded-lg shadow-md flex items-center justify-center space-x-2 transition-all duration-300 ${
                            isAdded
                                ? 'bg-white dark:bg-brand-dark border-2 border-amber-500 text-amber-600 shadow-amber-500/20'
                                : 'bg-amber-500 hover:bg-amber-600 text-white'
                        }`}
                    >
                        <FontAwesomeIcon icon={faClock} />
                        <span>{isAdded ? t('product.addedToCart') : t('product.addToCartMadeToOrder')}</span>
                    </motion.button>
                    <p className="text-center text-sm text-amber-600 dark:text-amber-400">
                        {t('product.productionTime')}
                    </p>
                </div>
            );
        }

        // In stock - show normal button
        return (
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onAddToCart}
                className={`w-full px-8 py-3 rounded-lg shadow-md flex items-center justify-center space-x-2 transition-all duration-300 ${
                    isAdded
                        ? 'bg-white dark:bg-brand-dark border-2 border-brand-blue text-brand-blue shadow-brand-blue/20'
                        : 'bg-brand-blue text-white'
                }`}
            >
                <FontAwesomeIcon icon={faShoppingCart} />
                <span>{isAdded ? t('product.addedToCart') : t('product.addToCart')}</span>
                {selectedVariant && <span className="text-sm ml-2">{t('product.stockLeft', { count: stock })}</span>}
            </motion.button>
        );
    };

    const MAX_QUANTITY_PER_ITEM = 5;

    const QuantitySelector = () => {
        const stock = getStock(product.id, selectedVariant?.id);
        const madeToOrder = isMadeToOrder(product.id, selectedVariant?.id);
        const isMadeToOrderItem = stock === 0 && madeToOrder;
        const maxAllowed = isMadeToOrderItem ? MAX_QUANTITY_PER_ITEM : Math.min(stock, MAX_QUANTITY_PER_ITEM);
        const atMaxQuantity = localQuantity >= maxAllowed;

        return (
            <div className="flex items-center justify-center gap-4 mt-4">
                <button
                    onClick={() => onQuantityChange(localQuantity - 1)}
                    className="px-3 py-1 bg-gray-200 dark:bg-brand-charcoal rounded disabled:opacity-50"
                    disabled={localQuantity <= 0}
                >
                    -
                </button>
                <span className="text-black dark:text-white">
                    {localQuantity}
                </span>
                <button
                    onClick={() => onQuantityChange(localQuantity + 1)}
                    className="px-3 py-1 bg-gray-200 dark:bg-brand-charcoal rounded disabled:opacity-50"
                    disabled={atMaxQuantity}
                >
                    +
                </button>
                {atMaxQuantity && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        {localQuantity >= MAX_QUANTITY_PER_ITEM ? t('cart.maxPerItem') : t('cart.maxStock')}
                    </span>
                )}
            </div>
        );
    };

    return (
        <>
            {/* Inline Add to Cart (mobile + desktop) */}
            <div ref={inlineRef}>
                <CartButton />
                {isAdded && !product?.oneOfKind && <QuantitySelector />}
            </div>

            {/* Fixed Bottom Mobile CTA — slides up only when inline CTA scrolls out of view */}
            <div className={`
                md:hidden fixed bottom-0 left-0 right-0 p-4
                bg-white dark:bg-brand-dark
                border-t border-gray-200 dark:border-brand-charcoal
                z-40 transition-transform duration-300
                ${(showFixedBar && !isCartOpen) ? 'translate-y-0' : 'translate-y-full'}
            `}>
                <CartButton />
                {isAdded && !product?.oneOfKind && <QuantitySelector />}
            </div>

            {/* Error message for variant selection */}
            {showVariantError && (
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 50 }}
                    className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg"
                >
                    {t('product.pleaseSelectMaterial')}
                </motion.div>
            )}
        </>
    );
};

export default CartActions; 